import { t } from "i18next";
import { Controller, useForm } from "react-hook-form";
import {
  useSalutationOptions,
  useTitleOptions,
} from "~/features/scanDmc/components/ContactFormOverlay/dmcContactFormUtils";
import { Box, Button, Stack, TextField, styled } from "~/common/mui";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import Modal from "~/common/components/Modal";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";

import { type UserInfoData } from "../types";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { useCountryOptions } from "../utils";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { PHONE_REGEXP, ZIPCODE_REGEXP } from "~/common/utils/validations";

const FormContainer = styled("form")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, auto)",
  gridTemplateRows: "repeat(6, auto)",
  columnGap: theme.spacing(3),
  rowGap: theme.spacing(2),
}));

const fill = { gridColumn: "span 2" };

const EmailSection = styled("div")(({ theme }) => ({
  ...fill,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  "& label": {
    color: theme.palette.text.secondary,
  },
}));

const CheckboxSection = styled("div")(({ theme }) => ({
  ...fill,
  marginBottom: theme.spacing(4),
}));

interface UserInfoModalProps {
  closeModal: () => void;
  formSubmit: (data: UserInfoData) => void;
  values: UserInfoData;
}

const UserInfoModal = ({ closeModal, formSubmit, values }: UserInfoModalProps) => {
  const { control, handleSubmit } = useForm({ defaultValues: { ...values } });

  const titleOptions = useTitleOptions();
  const salutationOptions = useSalutationOptions();
  const countryOptions = useCountryOptions();

  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  return (
    <Modal
      onClose={closeModal}
      title={t("request.userInformation")}
      footerChildren={
        <div className={`d-flex ${isTablet ? "flex-justify-between" : "flex-justify-end"} flex-align-center w-100`}>
          {isTablet && <div>{t("mandatory")}</div>}
          <Stack direction={"row"} gap={1}>
            <Button size={"medium"} onClick={closeModal} variant="text">
              {t("bulkUpload.buttons.cancel")}
            </Button>
            <Button size={"medium"} onClick={() => handleSubmit(formSubmit)()}>
              {t("editWishlistNamePopup.buttons.edit.ok")}
            </Button>
          </Stack>
        </div>
      }
    >
      <FormContainer>
        <Box sx={fill}>
          <Controller
            name={"company"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.completeRegistration.companyName") + "*"}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"titleCode"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <SimpleSelect
                inputRef={ref}
                fullWidth
                values={titleOptions}
                label={t("page.scandmc.form.title")}
                error={!!error}
                {...inputProps}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"salutationCode"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <SimpleSelect
                inputRef={ref}
                fullWidth
                values={salutationOptions}
                label={t("page.scandmc.form.salutation") + "*"}
                error={!!error}
                helperText={error?.message ?? null}
                {...inputProps}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"firstName"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.scandmc.form.firstName") + "*"}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"lastName"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.scandmc.form.lastName") + "*"}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"line1"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.completeRegistration.address")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"additionalLine1"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.completeRegistration.addressAdditional")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={!isTablet ? fill : undefined}>
          <Controller
            name={"zipCode"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.completeRegistration.zipCode")}
                error={!!error}
                inputRef={ref}
                inputProps={{
                  onBeforeInput: (e: any) => {
                    // @ts-ignore
                    const val = e.target.value + e.data;
                    if (!ZIPCODE_REGEXP.test(val.trim())) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  },
                }}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={!isTablet ? fill : undefined}>
          <Controller
            name={"city"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.completeRegistration.city")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"country"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
              <SimpleAutocomplete
                label={t("page.completeRegistration.country") + "*"}
                values={countryOptions}
                fullWidth
                error={!!error}
                onBlur={onBlur}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Box>

        <Box sx={fill}>
          <Controller
            name={"phone"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label={t("page.completeRegistration.phone")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperText={error?.message ?? null}
                inputProps={{
                  onBeforeInput: (e: any) => {
                    // @ts-ignore
                    const val = e.target.value + e.data;
                    if (!PHONE_REGEXP.test(val.trim())) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  },
                }}
              />
            )}
          />
        </Box>

        <EmailSection>
          <label className="label-large">{t("myCompany.form.email")}</label>
          <span className="title-medium">{window.SCHA.user.email}</span>
        </EmailSection>

        <CheckboxSection>
          <Controller
            name={"updateUser"}
            control={control}
            render={({ field: { value, ref, ...inputProps } }) => (
              <CheckboxWithLabel checked={value} inputRef={ref} {...inputProps}>
                {t("request.saveUserInfo")}
              </CheckboxWithLabel>
            )}
          />
        </CheckboxSection>
      </FormContainer>
    </Modal>
  );
};

export default UserInfoModal;
